import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { Badge, Box, chakra, HStack, Icon, Text } from '@chakra-ui/react';
import { LuChevronDown } from 'react-icons/lu';
import {
	DURATION_ACCORDION_CLOSE_MS,
	DURATION_ACCORDION_OPEN_MS,
	EASE_OUT,
	TRANSITION_CARD_HOVER,
} from '@/components/ui/motion';

interface CollapsibleCardProps {
	title: string;
	icon?: React.ReactNode;
	children: React.ReactNode;
	defaultOpen?: boolean;
	value: string;
	autoScrollOnOpen?: boolean;
	collapsible?: boolean;
	count?: number;
	/** Status shown on the heading row, before the chevron, for a card whose state reads at a glance. */
	meta?: React.ReactNode;
	/** Allow content to overflow the card (e.g. for absolute-positioned dropdowns) */
	overflowVisible?: boolean;
}

/**
 * Reusable collapsible card component with themed background and rounded borders
 * Uses Chakra UI v3 Accordion for collapsible functionality
 */
const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
	title,
	icon,
	children,
	defaultOpen = false,
	value,
	autoScrollOnOpen = false,
	collapsible = true,
	count,
	meta,
	overflowVisible = false,
}) => {
	const cardRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const contentId = useId();
	const triggerId = useId();
	const [isOpen, setIsOpen] = useState(defaultOpen || !collapsible);
	const [motionEnabled, setMotionEnabled] = useState(true);

	useLayoutEffect(() => {
		if (contentRef.current) contentRef.current.inert = !isOpen;
	}, [isOpen]);

	const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
		let current = element?.parentElement ?? null;

		while (current) {
			const styles = window.getComputedStyle(current);
			const overflowY = styles.overflowY;
			const isScrollable =
				(overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight;

			if (isScrollable) {
				return current;
			}

			current = current.parentElement;
		}

		return null;
	};

	const scrollCardIntoView = () => {
		const card = cardRef.current;
		if (!card) return;

		const scrollParent = findScrollParent(card);
		if (!scrollParent) return;

		const cardRect = card.getBoundingClientRect();
		const parentRect = scrollParent.getBoundingClientRect();
		const padding = 16;

		if (cardRect.bottom > parentRect.bottom) {
			scrollParent.scrollBy({
				top: cardRect.bottom - parentRect.bottom + padding,
				behavior: 'smooth',
			});
			return;
		}

		if (cardRect.top < parentRect.top) {
			scrollParent.scrollBy({
				top: cardRect.top - parentRect.top - padding,
				behavior: 'smooth',
			});
		}
	};

	const scheduleScrollIntoView = () => {
		let attempts = 0;

		const tick = () => {
			scrollCardIntoView();
			attempts += 1;

			if (attempts < 4) {
				window.setTimeout(tick, 120);
			}
		};

		window.setTimeout(tick, 140);
	};

	const handleToggle = (animate: boolean) => {
		if (!collapsible) return;

		const nextOpen = !isOpen;
		setMotionEnabled(animate);
		setIsOpen(nextOpen);
		if (autoScrollOnOpen && nextOpen) scheduleScrollIntoView();
		if (!animate) window.requestAnimationFrame(() => setMotionEnabled(true));
	};

	return (
		<Box
			layerStyle="card"
			ref={cardRef}
			_focus={{ outline: 'none', boxShadow: 'none' }}
			_focusVisible={{ outline: 'none', boxShadow: 'none' }}
			transition={TRANSITION_CARD_HOVER}
			css={{
				'@media (hover: hover) and (pointer: fine)': {
					'&:hover': {
						boxShadow: 'var(--chakra-shadows-sm)',
					},
				},
			}}
			data-state={isOpen ? 'open' : 'closed'}
			data-value={value}
		>
			<chakra.button
				id={triggerId}
				type="button"
				aria-expanded={isOpen}
				aria-controls={contentId}
				w="full"
				px={4}
				py={4}
				bg="transparent"
				border="none"
				// Matches the card it caps, so the inset focus ring follows the corner instead of
				// cutting a square across it.
				borderRadius="2xl"
				textAlign="left"
				cursor={collapsible ? 'pointer' : 'default'}
				onClick={(event) => handleToggle(event.detail > 0)}
				_focus={{ outline: 'none', boxShadow: 'none' }}
				_focusVisible={{ outline: '2px solid', outlineColor: 'brand.solid', outlineOffset: '-2px' }}
			>
				<HStack flex="1" justify="space-between" gap={2}>
					<HStack gap={2}>
						{icon}
						<Text
							textStyle="body.strong"
							fontSize="lg"
							color="brand.fg"
							lineHeight="1.5"
							css={{ '@media (max-width: 374px)': { fontSize: 'var(--chakra-font-sizes-md)' } }}
						>
							{title}
						</Text>
						{count !== undefined ? (
							<Badge colorPalette="brand" variant="subtle" size="sm" rounded="full" px={1.5}>
								{count}
							</Badge>
						) : null}
					</HStack>
					<HStack gap={2} minW={0}>
						{meta}
						{collapsible ? (
							<Icon
								as={LuChevronDown}
								boxSize={4}
								color="brand.fg"
								rotate={isOpen ? '180deg' : '0deg'}
								transition={
									motionEnabled
										? `rotate ${
												isOpen ? DURATION_ACCORDION_OPEN_MS : DURATION_ACCORDION_CLOSE_MS
											}ms ${EASE_OUT}`
										: 'none'
								}
								css={{
									'@media (prefers-reduced-motion: reduce)': {
										transition: 'none',
									},
								}}
							/>
						) : null}
					</HStack>
				</HStack>
			</chakra.button>

			<Box
				ref={contentRef}
				id={contentId}
				role="region"
				aria-labelledby={triggerId}
				aria-hidden={!isOpen}
				display="grid"
				gridTemplateRows={isOpen ? '1fr' : '0fr'}
				transition={
					motionEnabled
						? `grid-template-rows ${
								isOpen ? DURATION_ACCORDION_OPEN_MS : DURATION_ACCORDION_CLOSE_MS
							}ms ${EASE_OUT}`
						: 'none'
				}
				css={{
					'@media (prefers-reduced-motion: reduce)': {
						transition: 'none',
					},
				}}
			>
				<Box minH={0} overflow={isOpen && overflowVisible ? 'visible' : 'hidden'}>
					<Box px={4} pt={0} pb={4}>
						{children}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default CollapsibleCard;
